import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { FileArchive, TrendingUp, Eye, Filter } from 'lucide-react';

import ReportDialog from '../dialogs/ReportDialog';
import StatusChip from '../StatusChip';

const ReportsTab = ({ 
  isLoading, 
  setIsLoading, 
  currentUser, 
  showNotification, 
  logAction,
  handleViewProperty, // Función para ver propiedades (pasada desde StaffDashboard)
  handleViewUser      // Función para ver usuarios (pasada desde StaffDashboard)
}) => {
  // Report specific states
  const [reports, setReports] = useState([]);
  const [reportsPagination, setReportsPagination] = useState({ page: 0, rowsPerPage: 10, count: 0 });
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportResponse, setReportResponse] = useState('');
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Animation variant
  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      } 
    }
  };

  // Load reports on component mount and when filters change
  useEffect(() => {
    if (currentUser) {
      fetchReports();
    }
  }, [currentUser, statusFilter, reportsPagination.page, reportsPagination.rowsPerPage]);

  // Get reports list
  const fetchReports = async () => {
    setIsLoading(true);
    try {
      // Construct URL with filters
      let url = `/api/admin/reports?page=${reportsPagination.page}&limit=${reportsPagination.rowsPerPage}`;
      
      // Add status filter if not "all"
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await axios.get(url);
      setReports(response.data.reports);
      setReportsPagination({
        ...reportsPagination,
        count: response.data.total
      });
    } catch (error) {
      console.error('Error al obtener reportes:', error);
      showNotification('Error al cargar reportes', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Change page or rows per page
  const handleReportsPaginationChange = (event, newPage) => {
    setReportsPagination({
      ...reportsPagination,
      page: newPage
    });
  };
  
  const handleReportsRowsPerPageChange = (event) => {
    setReportsPagination({
      ...reportsPagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
  };
  
  // Apply filters
  const handleFilterChange = (status) => {
    setStatusFilter(status);
    setReportsPagination({
      ...reportsPagination,
      page: 0  // Reset to first page when changing filters
    });
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setStatusFilter('all');
    setReportsPagination({
      ...reportsPagination,
      page: 0  // Reset to first page when resetting filters
    });
  };
  
  // View report details
  const handleViewReport = (report) => {
    setSelectedReport(report);
    setReportResponse('');
    setReportDialogOpen(true);
    
    // Log action
    logAction(
      'REPORTE_VISTO',
      `Se ha visto el reporte #${report.id}`,
      report.id,
      'report'
    );
  };
  
  // Process a report (accept or reject)
  const handleProcessReport = async (action, additionalData = {}) => {
    if (!selectedReport) return;
    
    setIsLoading(true);
    try {
      // Usar los datos adicionales proporcionados por el diálogo
      const requestData = {
        action,
        response: reportResponse,
        ...additionalData // Incluir datos adicionales del propietario
      };
      
      console.log('Enviando petición para procesar reporte con datos:', requestData);
      
      const response = await axios.put(`/api/admin/reports/${selectedReport.id}/process`, requestData);
      
      // Determine the new status based on the action
      const newStatus = action === 'accept' ? 'resuelto' : 'revisado';
      
      // Mostrar información sobre emails enviados si está disponible
      if (response.data.emailsSent && response.data.emailsSent.length > 0) {
        const emailsInfo = response.data.emailsSent.map(e => 
          `${e.role === 'reporter' ? 'Usuario que reportó' : 'Propietario'}: ${e.email}`
        ).join(', ');
        showNotification(`Reporte ${action === 'accept' ? 'aceptado' : 'rechazado'} correctamente. Notificaciones enviadas a: ${emailsInfo}`, 'success');
      } else {
        showNotification(`Reporte ${action === 'accept' ? 'aceptado' : 'rechazado'} correctamente`, 'success');
      }
      
      logAction(
        `REPORTE_${action.toUpperCase()}`,
        `Se ha ${action === 'accept' ? 'aceptado' : 'rechazado'} el reporte #${selectedReport.id}: ${reportResponse}`,
        selectedReport.id,
        'report'
      );
      
      // If report was accepted, suspend the property
      if (action === 'accept') {
        await axios.put(`/api/admin/properties/${selectedReport.propiedad_id}/suspend`);
        logAction(
          'PROPIEDAD_SUSPENDIDA',
          `Se ha suspendido la propiedad ${selectedReport.propiedad_id} debido al reporte #${selectedReport.id}`,
          selectedReport.propiedad_id,
          'property'
        );
      }
      
      setReportDialogOpen(false);
      
      // Update the report in the local state to show the change immediately
      const updatedReports = reports.map(report => {
        if (report.id === selectedReport.id) {
          return {
            ...report,
            estado: newStatus,
            respuesta_admin: reportResponse,
            fecha_actualizacion: new Date().toISOString()
          };
        }
        return report;
      });
      
      // If we're filtering by status and the new status doesn't match the filter,
      // remove this report from the displayed list
      if (statusFilter !== 'all' && statusFilter !== newStatus) {
        setReports(updatedReports.filter(report => report.id !== selectedReport.id));
        // Adjust the count to reflect the removal
        setReportsPagination({
          ...reportsPagination,
          count: reportsPagination.count - 1
        });
      } else {
        setReports(updatedReports);
      }
      
    } catch (error) {
      console.error(`Error al ${action === 'accept' ? 'aceptar' : 'rechazar'} reporte:`, error);
      showNotification(`Error al procesar reporte: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Click on user to navigate to user details
  const handleUserClick = (userId, userName) => {
    if (userId && handleViewUser) {
      // Create a minimal user object to pass to the handler
      const user = {
        id: userId,
        nombre: userName || `Usuario ${userId}`
      };
      handleViewUser(user);
    }
  };

  // Click on property to view property details
  const handlePropertyClick = (propertyId) => {
    if (propertyId && handleViewProperty) {
      // Create a minimal property object
      const property = {
        id: propertyId,
        titulo: `Propiedad ${propertyId}`
      };
      handleViewProperty(property);
    }
  };

  return (
    <>
      <motion.div 
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        variants={slideUp}
      >
        <div className="p-6 flex justify-between items-center border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileArchive className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Gestión de Reportes</h2>
            {reports.filter(r => r.estado === 'pendiente').length > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold">
                {reports.filter(r => r.estado === 'pendiente').length}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`p-2 ${filtersOpen ? 'bg-indigo-100 text-indigo-700' : 'text-indigo-600 hover:bg-indigo-50'} rounded-full transition-colors`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Filter className="w-5 h-5" />
            </motion.button>
            <motion.button
              onClick={fetchReports}
              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <TrendingUp className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
        
        {/* Filters Section */}
        <AnimatePresence>
          {filtersOpen && (
            <motion.div 
              className="bg-indigo-50/50 border-b border-indigo-100 p-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Estado</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    className="px-3 py-2 bg-white border border-indigo-200 rounded-lg text-sm text-gray-700 
                              focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">Todos</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="revisado">Revisado</option>
                    <option value="impugnado">Impugnado</option>
                    <option value="resuelto">Resuelto</option>
                  </select>
                </div>
                
                <motion.button
                  onClick={handleResetFilters}
                  className="px-4 py-2 mt-5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Limpiar filtros
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-indigo-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Propiedad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Reportado por</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-indigo-800 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{report.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer" 
                        onClick={() => handlePropertyClick(report.propiedad_id)}>
                      {report.propiedad_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer"
                        onClick={() => handleUserClick(report.usuario_id, report.usuario_nombre)}>
                      {report.usuario_nombre || report.usuario_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {report.tipo_reporte === 'no_disponible' && 'No disponible'}
                      {report.tipo_reporte === 'estafa' && 'Estafa'}
                      {report.tipo_reporte === 'otros' && 'Otros'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(report.fecha_reporte).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusChip status={report.estado} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <motion.button
                        onClick={() => handleViewReport(report)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No hay reportes disponibles
                    {statusFilter !== 'all' && (
                      <span> con el filtro seleccionado</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {reportsPagination.count > 0 ? (
              <span>
                Mostrando {reportsPagination.page * reportsPagination.rowsPerPage + 1} a{' '}
                {Math.min((reportsPagination.page + 1) * reportsPagination.rowsPerPage, reportsPagination.count)} de{' '}
                {reportsPagination.count} reportes
                {statusFilter !== 'all' && (
                  <span> ({statusFilter})</span>
                )}
              </span>
            ) : (
              <span>No hay reportes disponibles</span>
            )}
          </div>
          
          <div className="flex gap-2">
            <select
              value={reportsPagination.rowsPerPage}
              onChange={(e) => handleReportsRowsPerPageChange(e)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value={5}>5 / página</option>
              <option value={10}>10 / página</option>
              <option value={25}>25 / página</option>
              <option value={50}>50 / página</option>
            </select>
            
            <div className="flex">
              <button
                onClick={() => handleReportsPaginationChange(null, reportsPagination.page - 1)}
                disabled={reportsPagination.page === 0}
                className={`px-3 py-1 border ${
                  reportsPagination.page === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                } rounded-l-lg transition-colors`}
              >
                Anterior
              </button>
              <button
                onClick={() => handleReportsPaginationChange(null, reportsPagination.page + 1)}
                disabled={reportsPagination.page >= Math.ceil(reportsPagination.count / reportsPagination.rowsPerPage) - 1}
                className={`px-3 py-1 border border-l-0 ${
                  reportsPagination.page >= Math.ceil(reportsPagination.count / reportsPagination.rowsPerPage) - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                } rounded-r-lg transition-colors`}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Report Details Dialog */}
      <ReportDialog 
        reportDialogOpen={reportDialogOpen} 
        setReportDialogOpen={setReportDialogOpen}
        selectedReport={selectedReport}
        reportResponse={reportResponse}
        setReportResponse={setReportResponse}
        handleProcessReport={handleProcessReport}
        handleViewProperty={handleViewProperty}
        handleViewUser={handleViewUser}
      />
    </>
  );
};

export default ReportsTab;